/**
 * My Scenarios library (Phase 5a). Lists every scenario in the repository,
 * most-recently-updated first, with per-row Open / Duplicate / Delete actions
 * and a New scenario button. The currently-open scenario is marked.
 *
 * The list is loaded fresh each time the modal opens (via an effect), so it
 * reflects creates/deletes that happened in this session. With localStorage
 * the read is instant; with Supabase (Phase 5b) it's a network round-trip,
 * which is why the list shows a brief loading state.
 */
import { useEffect, useState } from 'react';
import { FilePlus, FolderOpen, Copy, Trash2, Check, LogOut } from 'lucide-react';
import { useScenarioStore } from '../store/scenarioStore';
import { Modal } from './Modal';
import { getRepository, isCloudPersistence } from '../persistence';
import type { ScenarioSummary } from '../persistence/ScenarioRepository';

export function LibraryModal() {
  const setOpenModal = useScenarioStore((s) => s.setOpenModal);
  const currentId = useScenarioStore((s) => s.scenarioId);
  const openScenario = useScenarioStore((s) => s.openScenario);
  const createScenario = useScenarioStore((s) => s.createScenario);
  const duplicateScenario = useScenarioStore((s) => s.duplicateScenario);
  const deleteScenario = useScenarioStore((s) => s.deleteScenario);

  const [summaries, setSummaries] = useState<ScenarioSummary[] | null>(null);

  // Load the list whenever the modal mounts.
  const refresh = async () => {
    const list = await getRepository().list();
    setSummaries(list);
  };
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      kicker="Persistence"
      title="My scenarios"
      onClose={() => setOpenModal(null)}
      maxWidth={720}
      footer={
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={() => void createScenario()}
            className="px-4 py-2 rounded text-sm flex items-center gap-1.5 bg-ink text-cream-2"
          >
            <FilePlus size={14} /> New scenario
          </button>
          <CloudAccountControl />
        </div>
      }
    >
      {summaries === null ? (
        <div className="text-sm italic text-ink-3 text-center py-8">Loading…</div>
      ) : summaries.length === 0 ? (
        <div className="text-sm italic text-center py-8 px-4 rounded border border-dashed border-cream-border text-ink-2">
          <div className="font-bold not-italic mb-1 text-ink">No scenarios yet.</div>
          Use <span className="not-italic font-bold">New scenario</span> below to start one.
        </div>
      ) : (
        <div className="space-y-2">
          {summaries.map((s) => (
            <ScenarioRow
              key={s.id}
              summary={s}
              isCurrent={s.id === currentId}
              onOpen={() => void openScenario(s.id)}
              onDuplicate={async () => {
                await duplicateScenario(s.id);
              }}
              onDelete={async () => {
                if (
                  confirm(
                    `Delete "${s.title}"? This removes the draft and all its published versions from local storage. Export first if you want a copy.`,
                  )
                ) {
                  await deleteScenario(s.id);
                  await refresh();
                }
              }}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}

interface ScenarioRowProps {
  summary: ScenarioSummary;
  isCurrent: boolean;
  onOpen: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function ScenarioRow({ summary, isCurrent, onOpen, onDuplicate, onDelete }: ScenarioRowProps) {
  const niceDate = formatUpdatedAt(summary.updatedAt);
  return (
    <div
      className="rounded border p-3 flex items-center gap-3"
      style={{
        borderColor: isCurrent ? '#B8551F' : '#E5DFD0',
        background: isCurrent ? '#FBF1EA' : '#FFFEFB',
      }}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {isCurrent && <Check size={13} className="flex-shrink-0 text-terracotta" />}
          <span className="font-bold text-ink truncate">{summary.title}</span>
        </div>
        <div className="text-[11px] text-ink-3 mt-0.5">
          Updated {niceDate}
          {summary.publishedCount > 0 &&
            ` · ${summary.publishedCount} published version${summary.publishedCount === 1 ? '' : 's'}`}
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {!isCurrent && (
          <button
            onClick={onOpen}
            className="p-1.5 rounded hover:bg-cream text-ink-2"
            title={`Open "${summary.title}"`}
            aria-label={`Open ${summary.title}`}
          >
            <FolderOpen size={14} />
          </button>
        )}
        <button
          onClick={onDuplicate}
          className="p-1.5 rounded hover:bg-cream text-ink-2"
          title={`Duplicate "${summary.title}"`}
          aria-label={`Duplicate ${summary.title}`}
        >
          <Copy size={14} />
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 rounded hover:bg-cream text-terracotta-dark"
          title={`Delete "${summary.title}"`}
          aria-label={`Delete ${summary.title}`}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

function formatUpdatedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Account control shown in the Library footer — cloud mode only. Displays the
 * signed-in email and a sign-out button. Renders nothing in local mode, where
 * there's no account. Auth helpers are imported dynamically so the
 * Supabase-touching code stays out of the local-only bundle.
 */
function CloudAccountControl() {
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    if (!isCloudPersistence()) return;
    let mounted = true;
    void import('../persistence/supabaseClient').then(({ getCurrentUserEmail }) =>
      getCurrentUserEmail().then((e) => {
        if (mounted) setEmail(e);
      }),
    );
    return () => {
      mounted = false;
    };
  }, []);

  if (!isCloudPersistence()) return null;

  const onSignOut = async () => {
    const { signOut } = await import('../persistence/supabaseClient');
    await signOut();
    // onAuthChange in the AuthGate flips back to the sign-in screen.
  };

  return (
    <div className="flex items-center gap-3 text-xs text-ink-2">
      {email && (
        <span className="truncate max-w-[200px]" title={email}>
          {email}
        </span>
      )}
      <button
        onClick={() => void onSignOut()}
        className="flex items-center gap-1 hover:text-ink hover:underline"
        title="Sign out"
      >
        <LogOut size={13} /> Sign out
      </button>
    </div>
  );
}
